import type { KpiCategory } from 'lumen/utils/constants/ri-constant-values';
import { isAPIReference } from 'lumen/utils/revenue-insight/ri-utils';

interface Params {
  hidePickupLY: boolean;
  kpiCategory: KpiCategory;
  refFromDate?: string;
  reference: string;
  segmentationFilters: string;
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function generateGeneralUrl(
  baseUrl: string,
  segmentations: Record<string, unknown>,
  visibleCategoryMappings: string[],
  params: Params,
  useFilters: boolean,
) {
  let url = baseUrl;

  Object.keys(segmentations).forEach((segmentationObjName) => {
    if (segmentationObjName !== 'isLoading') {
      url += `&aggregations[]=${segmentationObjName}`;
    }
  });

  visibleCategoryMappings.forEach((category) => {
    url += `&category_aggregations[]=${category}`;
  });

  if (useFilters && params.segmentationFilters) {
    // We need to encode these filters before we can add them to the URL
    const filters = encodeURIComponent(params.segmentationFilters);

    url += `&filters=${filters}`;
  }

  url += `&disable_pickup_ly=${params.hidePickupLY}`;
  url += `&kpi_category=${params.kpiCategory}`;

  return url;
}

/**
 * Generates a URL for the data-engineering debug tool (de-tools.mylighthouse.com).
 * @param  { string } host           : de-tools base URL (from config.deToolsURL)
 * @param  { string } path           : de-tools route path (e.g. /ui/spanner/reservations)
 * @param  { number } ourIntegrationId  : PMS integration id (our_integration_id)
 * @param  { string } extractDate    : date at which the requested data was extracted
 * @param  { object } segmentations  : current model, needed for the segmentation names
 * @param  { object } visibleCategoryMappings  : segmentation ids for which to enable PMS category mapping
 * @param  { string } dateRangeStart : start date of data range we want to request
 * @param  { string } dateRangeEnd   : end date of data range we want to request
 * @param  { object } params         : URL parameters from this route, needed for some reference parameters
 * @param  { boolean } useFilters    : Boolean whether filters should be included in the request
 * @return { string } A URL which can be used to deep-link into the de-tools debug view
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export default function generateSingleURL(
  host: string,
  path: string,
  ourIntegrationId: number,
  extractDate: string,
  segmentations: Record<string, unknown>,
  visibleCategoryMappings: string[],
  dateRangeStart: string,
  dateRangeEnd: string,
  params: Params,
  useFilters = false,
) {
  let url = `${host}${path}?our_integration_id=${ourIntegrationId}&start_date=${dateRangeStart}&end_date=${dateRangeEnd}`;

  if (extractDate) {
    url += `&extract_date=${extractDate}`;
  }

  if (isAPIReference(params.reference)) {
    url += `&ref_period=${params.reference}`;
  } else {
    url += `&ref_period=${params.refFromDate}`;
  }

  return generateGeneralUrl(
    url,
    segmentations,
    visibleCategoryMappings,
    params,
    useFilters,
  );
}
